/**
 * Shared quiz-answering component used across Practice, Duel, and Tournament modes.
 *
 * Props:
 *   quiz           - The quiz object (incognito — options do NOT contain is_answer)
 *   onDone         - (quizId, subjectLevelInfo?) => void
 *   onBack         - optional () => void — shows back button when provided
 *   accentColor    - primary accent color string, e.g. "#42A5F5"
 *   mode           - badge label: "PRACTICE" | "DUEL" | "TOURNAMENT"
 *   tournamentCode - optional string, added to submit-quiz payload
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import { ArrowBack, CheckCircle } from "@mui/icons-material";
import { keyframes } from "@emotion/react";
import MathContent from "../../../../customComponents/MathContent";
import {
  useSubmitQuizMutation,
  useCheckAnswerMutation,
  useStartQuizMutation,
} from "../../../../../app/quizApiSlice";
import db from "../../../../../app/offlineDb";

// ─── Per-option colour palette (label colours only — selection/reveal handled by state) ─────
const OPTION_COLORS = [
  { base: "#3b82f6" },
  { base: "#f97316" },
  { base: "#22c55e" },
  { base: "#a855f7" },
];

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.92) translateY(10px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

const slideQ = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to   { opacity: 1; transform: translateX(0); }
`;

// ─── Component ────────────────────────────────────────────────────────────────
const QuizPlayer = ({
  quiz,
  onDone,
  onBack,
  accentColor = "#42A5F5",
  mode = "PRACTICE",
  tournamentCode,
}) => {
  const questions = quiz.questions || [];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState(
    questions.map((q) => ({ question_id: q.id, option_id: 0 }))
  );
  const [revealed, setRevealed] = useState(false);
  // correctOptionId: set by the check-answer API after each selection
  const [correctOptionId, setCorrectOptionId] = useState(null);
  const [correctOptionBody, setCorrectOptionBody] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [timeLeft, setTimeLeft] = useState(quiz.duration ? quiz.duration * 60 : 0);

  // Per-question reveal cache — keyed by question_id. Populated as the student
  // answers questions in this session and restored from IndexedDB on resume.
  // Stored in a ref (not state) so the question-change effect always reads the
  // latest value without needing it in the dependency array.
  const revealMapRef = useRef({});

  // Derive userId for IndexedDB keying (same approach as PracticeQuiz.jsx)
  const userId = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user"))?.id ?? 0; } catch { return 0; }
  }, []);

  const [submitQuiz, { isLoading: isSubmitting }] = useSubmitQuizMutation();
  const [checkAnswer, { isLoading: isChecking }] = useCheckAnswerMutation();
  const [startQuiz] = useStartQuizMutation();
  const answersRef = useRef(answers);
  const timerInitialisedRef = useRef(false);

  useEffect(() => { answersRef.current = answers; }, [answers]);

  // ── Restore in-progress session from IndexedDB ────────────────────────────
  useEffect(() => {
    if (!quiz.id || !userId || mode !== "PRACTICE") return;
    db.quizSessions.get([quiz.id, userId]).then((session) => {
      if (!session) return;
      if (session.answers?.length) {
        setAnswers(session.answers);
        answersRef.current = session.answers;
        // Jump to the first unanswered question so the student continues where they left off
        const firstUnanswered = session.answers.findIndex((a) => !a.option_id);
        setCurrentIndex(firstUnanswered >= 0 ? firstUnanswered : session.answers.length - 1);
      }
      if (session.revealMap) {
        revealMapRef.current = session.revealMap;
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quiz.id, userId]);

  // ── Persist answers + reveal results to IndexedDB ─────────────────────────
  const persistSession = useCallback(async (updatedAnswers, updatedRevealMap) => {
    if (!userId || mode !== "PRACTICE") return;
    try {
      const existing = await db.quizSessions.get([quiz.id, userId]);
      const record = existing || { quizId: quiz.id, studentId: userId, status: "in-progress" };
      await db.quizSessions.put({
        ...record,
        answers: updatedAnswers,
        revealMap: updatedRevealMap,
        updatedAt: Date.now(),
      });
    } catch (_) {}
  }, [quiz.id, userId, mode]);

  // ── Server-anchored timer ──────────────────────────────────────────────────
  // On mount, call /quiz/<id>/start to get a server-recorded started_at
  // timestamp. This makes the countdown survive refreshes: the remaining time
  // is always (duration * 60) - (now - started_at), so a page reload simply
  // recalculates rather than restarting at the full duration.
  useEffect(() => {
    if (!quiz.duration || timerInitialisedRef.current) return;
    timerInitialisedRef.current = true;

    startQuiz(quiz.id)
      .unwrap()
      .then(({ started_at, server_time }) => {
        const startMs = new Date(started_at).getTime();
        const nowMs = new Date(server_time).getTime();
        const elapsedSeconds = Math.floor((nowMs - startMs) / 1000);
        const remaining = Math.max(0, quiz.duration * 60 - elapsedSeconds);
        setTimeLeft(remaining);
        if (remaining <= 0) {
          // Already expired by the time the component mounted — auto-submit
          handleSubmit();
        }
      })
      .catch(() => {
        // Network error: fall back to local duration so the UI is not broken
        setTimeLeft(quiz.duration * 60);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quiz.id, quiz.duration]);

  useEffect(() => {
    if (!quiz.duration) return;
    const timer = setInterval(() => setTimeLeft((t) => (t > 0 ? t - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, [quiz.duration]);

  useEffect(() => {
    if (quiz.duration && timeLeft <= 0) handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  // ── Reset per-question state when question changes ─────────────────────────
  // If the student navigates to a question they already answered in this session
  // (e.g. after restoring from IndexedDB), restore the reveal state.
  useEffect(() => {
    const questionId = questions[currentIndex]?.id;
    const savedReveal = revealMapRef.current[questionId];
    const savedAnswer = answersRef.current[currentIndex]?.option_id;

    if (savedReveal && savedAnswer) {
      setRevealed(true);
      setCorrectOptionId(savedReveal.correctOptionId);
      setCorrectOptionBody(savedReveal.correctOptionBody);
      setExplanation(savedReveal.explanation);
    } else {
      setRevealed(false);
      setCorrectOptionId(null);
      setCorrectOptionBody(null);
      setExplanation(null);
    }
  }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  // ── Answer selection → calls check-answer API and persists to IndexedDB ────
  const handleAnswer = async (optionId) => {
    if (revealed || isChecking) return;
    const updated = [...answers];
    updated[currentIndex].option_id = optionId;
    setAnswers(updated);
    answersRef.current = updated;

    // Reveal immediately with loading state, then fill in correct answer
    setRevealed(true);
    try {
      const result = await checkAnswer({
        quiz_id: quiz.id,
        question_id: questions[currentIndex].id,
      }).unwrap();
      setCorrectOptionId(result.correct_option_id);
      setCorrectOptionBody(result.correct_option_body);
      setExplanation(result.explanation);
      // Cache reveal for this question so it survives navigation / restore
      revealMapRef.current = {
        ...revealMapRef.current,
        [questions[currentIndex].id]: {
          correctOptionId: result.correct_option_id,
          correctOptionBody: result.correct_option_body,
          explanation: result.explanation,
        },
      };
    } catch (_) {
      // If check-answer fails (network etc.) just leave revealed with no highlight — submit still works
    }
    // Persist regardless of whether check-answer succeeded
    persistSession(updated, revealMapRef.current);
  };

  const handleSubmit = async () => {
    let subjectLevelInfo = null;
    try {
      const data = await submitQuiz({
        quiz_id: quiz.id,
        answers,
        ...(tournamentCode ? { tournament_code: tournamentCode } : {}),
      }).unwrap();
      subjectLevelInfo = data?.subject_level || null;
    } catch (e) {
      console.error(e);
    }
    // Clear the persisted session — quiz is done
    if (userId && mode === "PRACTICE") {
      db.quizSessions.delete([quiz.id, userId]).catch(() => {});
    }
    onDone(quiz.id, subjectLevelInfo);
  };

  const question = questions[currentIndex];
  const selectedOptionId = answers[currentIndex]?.option_id;
  const isCorrect = revealed && correctOptionId !== null && selectedOptionId === correctOptionId;

  if (!question) return null;

  const isLast = currentIndex === questions.length - 1;
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const timerCritical = quiz.duration > 0 && timeLeft < 30;

  // accent-coloured hex with alpha helper
  const accentAlpha = (alpha) => {
    // works for "#rrggbb" strings
    const r = parseInt(accentColor.slice(1, 3), 16);
    const g = parseInt(accentColor.slice(3, 5), 16);
    const b = parseInt(accentColor.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  return (
    <Box>
      {/* ── HUD bar ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex", alignItems: "center", gap: 1,
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "16px", px: 1.25, py: 0.75, mb: 2,
          backdropFilter: "blur(10px)",
        }}
      >
        {onBack && (
          <IconButton
            onClick={onBack}
            size="small"
            sx={{ color: "rgba(255,255,255,0.7)", bgcolor: "rgba(255,255,255,0.07)", borderRadius: "10px", "&:hover": { bgcolor: "rgba(255,255,255,0.13)" } }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
        )}
        <Box sx={{ px: 1, py: 0.35, borderRadius: "7px", bgcolor: accentAlpha(0.18), border: `1px solid ${accentAlpha(0.35)}`, ml: 0.25 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "0.65rem", color: accentColor, letterSpacing: 1 }}>{mode}</Typography>
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "rgba(255,255,255,0.75)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ml: 0.5 }}>
          {quiz.subject}
        </Typography>
        <Box sx={{ textAlign: "center", minWidth: 34, mr: 0.5 }}>
          <Typography sx={{ fontWeight: 900, fontSize: "0.95rem", color: "#fff", lineHeight: 1 }}>
            {currentIndex + 1}<Typography component="span" sx={{ fontWeight: 500, fontSize: "0.68rem", color: "rgba(255,255,255,0.5)" }}>/{questions.length}</Typography>
          </Typography>
          <Typography sx={{ fontSize: "0.55rem", color: "rgba(255,255,255,0.45)", fontWeight: 600, letterSpacing: 0.8 }}>Q</Typography>
        </Box>
        {quiz.duration > 0 && (
          <Box sx={{ bgcolor: timerCritical ? "rgba(239,68,68,0.8)" : "rgba(255,255,255,0.09)", borderRadius: "8px", px: 1.1, py: 0.45, transition: "background-color 0.5s" }}>
            <Typography sx={{ fontWeight: 900, fontSize: "0.9rem", color: "#fff", fontFamily: "monospace" }}>
              {formatTime(timeLeft)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Progress bar ────────────────────────────────────────────────── */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 5, borderRadius: "4px", mb: 2.5,
          bgcolor: "rgba(255,255,255,0.07)",
          "& .MuiLinearProgress-bar": {
            background: `linear-gradient(90deg, ${accentColor} 0%, ${accentAlpha(0.7)} 100%)`,
            borderRadius: "4px",
          },
        }}
      />

      {/* ── Question card ────────────────────────────────────────────────── */}
      <Box
        key={currentIndex}
        sx={{
          p: { xs: 2.5, sm: 3 }, mb: 2.5,
          background: "rgba(255,255,255,0.05)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: "18px", textAlign: "center",
          animation: `${slideQ} 0.22s ease`,
        }}
      >
        <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: "10px", bgcolor: accentAlpha(0.2), border: `1px solid ${accentAlpha(0.35)}`, mb: 1.5 }}>
          <Typography sx={{ fontWeight: 900, fontSize: "0.8rem", color: accentColor }}>{currentIndex + 1}</Typography>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.7, fontSize: { xs: "0.98rem", sm: "1.08rem" }, color: "#fff" }}>
          <MathContent html={question?.body} />
        </Typography>
      </Box>

      {/* ── Answer options ───────────────────────────────────────────────── */}
      <Box sx={{ mb: revealed ? 1 : 3 }}>
        {question?.options?.map((opt, idx) => {
          const color = OPTION_COLORS[idx % 4];
          const isSelected = selectedOptionId === opt.id;
          const isTheCorrectOne = revealed && correctOptionId !== null && opt.id === correctOptionId;
          const isTheWrongSelected = revealed && isSelected && !isTheCorrectOne;

          let bg, borderColor, textColor, boxShadow = "none";
          if (!revealed) {
            if (isSelected) {
              bg = `${color.base}28`; borderColor = color.base; textColor = "#fff"; boxShadow = `0 0 18px ${color.base}4D`;
            } else {
              bg = "rgba(255,255,255,0.04)"; borderColor = "rgba(255,255,255,0.1)"; textColor = "rgba(255,255,255,0.78)";
            }
          } else if (isTheCorrectOne) {
            bg = "rgba(34,197,94,0.15)"; borderColor = "#22c55e"; textColor = "#fff"; boxShadow = "0 0 20px rgba(34,197,94,0.2)";
          } else if (isTheWrongSelected) {
            bg = "rgba(239,68,68,0.15)"; borderColor = "#ef4444"; textColor = "rgba(255,255,255,0.65)";
          } else {
            bg = "rgba(255,255,255,0.02)"; borderColor = "rgba(255,255,255,0.06)"; textColor = "rgba(255,255,255,0.3)";
          }

          return (
            <Box
              key={opt.id}
              onClick={() => handleAnswer(opt.id)}
              sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                p: "12px 16px", mb: 1.25, borderRadius: "14px",
                background: bg, border: `1.5px solid ${borderColor}`, boxShadow,
                cursor: revealed ? "default" : "pointer",
                transition: "all 0.15s ease",
                animation: `${popIn} 0.25s ${idx * 0.06}s ease both`,
                "&:hover": !revealed && !isSelected ? { background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.22)" } : {},
              }}
            >
              <Box
                sx={{
                  width: 30, height: 30, borderRadius: "9px",
                  bgcolor: revealed && !isTheCorrectOne && !isSelected ? "rgba(255,255,255,0.1)" : color.base,
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  transition: "background-color 0.2s",
                }}
              >
                <Typography sx={{ fontWeight: 900, fontSize: "0.78rem", color: "#fff" }}>
                  {["A", "B", "C", "D"][idx] || idx + 1}
                </Typography>
              </Box>
              <Typography sx={{ flex: 1, fontWeight: isTheCorrectOne ? 800 : 600, fontSize: { xs: "0.9rem", sm: "0.95rem" }, lineHeight: 1.45, color: textColor }}>
                <MathContent html={opt.body} />
              </Typography>
              {isTheCorrectOne && <CheckCircle sx={{ color: "#4ade80", fontSize: 20, flexShrink: 0 }} />}
              {isTheWrongSelected && <Typography sx={{ color: "#f87171", fontWeight: 900, fontSize: "1rem", flexShrink: 0, lineHeight: 1 }}>✕</Typography>}
            </Box>
          );
        })}
      </Box>

      {/* Feedback banner — shown only once check-answer resolves */}
      {revealed && correctOptionId !== null && (
        <Box
          sx={{
            p: 2, mb: 2, borderRadius: "14px",
            background: isCorrect ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
            border: `1.5px solid ${isCorrect ? "rgba(34,197,94,0.4)" : "rgba(239,68,68,0.4)"}`,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 800, color: isCorrect ? "#4ade80" : "#f87171", mb: 0.5, fontSize: "0.9rem" }}>
            {isCorrect
              ? "✓ Correct! Well done!"
              : <><span>✗ Not quite — the answer was: </span><MathContent html={correctOptionBody} /></>}
          </Typography>
          {explanation && (
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)", mt: 0.5 }}>
              <MathContent text={explanation} />
            </Typography>
          )}
        </Box>
      )}

      {/* Skeleton feedback area while waiting for check-answer */}
      {revealed && correctOptionId === null && isChecking && (
        <Box sx={{ height: 52, mb: 2, borderRadius: "14px", bgcolor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={16} sx={{ color: "rgba(255,255,255,0.3)" }} />
        </Box>
      )}

      {/* Navigation buttons */}
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        {revealed && (
          isLast ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || isChecking}
              sx={{
                background: "linear-gradient(90deg, #16a34a 0%, #15803d 100%)",
                fontWeight: 800, px: 3, borderRadius: "10px",
                boxShadow: "0 4px 12px rgba(22,163,74,0.4)",
              }}
            >
              {isSubmitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Submit Quiz"}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => setCurrentIndex((i) => i + 1)}
              disabled={isChecking}
              sx={{
                background: `linear-gradient(90deg, ${accentColor} 0%, ${accentAlpha(0.75)} 100%)`,
                fontWeight: 800, px: 3, borderRadius: "10px",
                boxShadow: `0 4px 12px ${accentAlpha(0.4)}`,
              }}
            >
              Next →
            </Button>
          )
        )}
      </Box>
    </Box>
  );
};

export default QuizPlayer;
